import { mdiRefresh } from '@mdi/js';
import { Icon } from '@mdi/react';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { format, formatDistanceToNow } from 'date-fns';
import { cs } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { defineStyles } from '../../theme';

const styles = defineStyles({
  iconButton: {
    padding: 0.5,
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  orangeDotBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: 'warning.main',
      minWidth: 8,
      height: 8,
      borderRadius: '50%',
    },
  },
  menuPaper: {
    borderRadius: 2,
    boxShadow: '0px 4px 20px rgba(0,0,0,0.12)',
    border: '1px solid',
    borderColor: 'divider',
    minWidth: 260,
  },
  autoRow: {
    py: 1,
  },
  lastChangeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 0.5,
    px: 1.25,
  },
});

export type RefreshSampleButtonProps = {
  tableStatus: {
    lastUpdated: string | null;
    totalSize: number;
  };
  serverStatus: {
    lastUpdated: string | null;
    size: number;
    totalSize: number;
  };
  /** Manual "Reload now" click. Wipes rows and re-fetches from the top. */
  onReload: () => void;
  /**
   * Background status-driven auto-reload. Should preserve the user's scroll
   * position by re-fetching only the currently visible window.
   */
  onAutoReload: () => void;
  onTotalSizeChange: () => void;
};

function isSampleDataStale(
  server: RefreshSampleButtonProps['serverStatus'],
  table: RefreshSampleButtonProps['tableStatus']
  // toleranceMs: number
): boolean {
  // Sample row count out of sync with server → need reload.
  if (server.size !== table.totalSize) {
    return true;
  }

  const serverTs = server.lastUpdated;
  const tableTs = table.lastUpdated;
  // Both sides lack a usable timestamp → nothing to compare, treat as fresh.
  if (!serverTs && !tableTs) {
    return false;
  }
  // Exactly one side has a timestamp → mismatch until both agree.
  if (!serverTs || !tableTs) {
    return true;
  }
  // Both present: stale only if server is newer than table beyond tolerance (avoids reload loops on tiny skew).
  return new Date(serverTs).getTime() > new Date(tableTs).getTime();
}

export default function RefreshSampleButton({
  tableStatus = { lastUpdated: null, totalSize: 0 },
  serverStatus = { lastUpdated: null, size: 0, totalSize: 0 },
  onReload,
  onAutoReload,
  onTotalSizeChange,
}: RefreshSampleButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [autoreload, setAutoreload] = useState(true);
  const prevTotalSizeRef = useRef<number | undefined>(undefined);
  const prevStaleKeyRef = useRef<string | null | undefined>(undefined);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const prevValue = prevTotalSizeRef.current;
    const currentValue = serverStatus.totalSize;

    if (prevValue !== undefined && prevValue !== currentValue) {
      onTotalSizeChange();
    }

    prevTotalSizeRef.current = currentValue;
  }, [serverStatus.totalSize, onTotalSizeChange]);

  const hasStaleData = isSampleDataStale(serverStatus, tableStatus);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAutoreloadToggle = () => {
    setAutoreload((prev) => !prev);
  };

  const isTableInitialized = Boolean(tableStatus.lastUpdated) || tableStatus.totalSize > 0;
  const staleKey = `${serverStatus.lastUpdated ?? ''}|${serverStatus.size}|${tableStatus.lastUpdated ?? ''}`;

  useEffect(() => {
    if (!autoreload || !hasStaleData || !isTableInitialized) return;

    if (staleKey !== prevStaleKeyRef.current) {
      prevStaleKeyRef.current = staleKey;
      onAutoReload();
    }
  }, [autoreload, hasStaleData, isTableInitialized, onAutoReload, staleKey]);

  const showOrangeDot = hasStaleData && !autoreload;

  const icon = autoreload ? (
    <AutoModeIcon sx={{ fontSize: 24, color: 'white' }} />
  ) : (
    <Icon path={mdiRefresh} size={1} />
  );

  return (
    <>
      <Tooltip title={<FormattedMessage id="tooltip.refresh" defaultMessage="Refresh" />}>
        <IconButton
          onClick={handleClick}
          sx={styles.iconButton}
          aria-controls={open ? 'refresh-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge variant="dot" invisible={!showOrangeDot} sx={styles.orangeDotBadge}>
            {icon}
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id="refresh-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: styles.menuPaper,
          },
        }}
      >
        <MenuList dense disablePadding>
          <MenuItem onClick={onReload}>
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiRefresh} size={1} />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="refresh.menu.reloadNow" defaultMessage="Reload now" />} />
          </MenuItem>
          <MenuItem onClick={handleAutoreloadToggle} disableRipple sx={styles.autoRow}>
            <ListItemIcon sx={{ minWidth: 32, color: autoreload ? 'primary.main' : 'text.secondary' }}>
              <AutoModeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="refresh.menu.autoreload" defaultMessage="Auto reload" />} />
            <Switch
              edge="end"
              checked={autoreload}
              onChange={handleAutoreloadToggle}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
            />
          </MenuItem>
          <Divider />
          {tableStatus.lastUpdated && (
            <ListItem
              disablePadding
              sx={{
                ...styles.lastChangeRow,
                cursor: 'default',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: 11, color: 'grey.600', flexShrink: 0 }}>
                <FormattedMessage id="refresh.menu.lastChange" defaultMessage="Poslední změna" />
              </Typography>
              <Tooltip title={format(new Date(tableStatus.lastUpdated), 'd.M.yyyy HH:mm:ss', { locale: cs })}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', cursor: 'help', flexShrink: 0 }}
                >
                  {formatDistanceToNow(new Date(tableStatus.lastUpdated), {
                    addSuffix: true,
                    includeSeconds: true,
                    locale: cs,
                  })}
                </Typography>
              </Tooltip>
            </ListItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
}
